import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export const parseFirestoreTimestamp = (ts) => {
  if (!ts) return null;
  if (ts?.toDate && typeof ts.toDate === "function") return dayjs(ts.toDate());
  if (ts?.seconds != null) return dayjs(ts.seconds * 1000);
  return null;
};

/** Approved deposits / paid withdrawals — use conclusion or approval time. */
export const getFinishedTimestamp = (row) =>
  parseFirestoreTimestamp(row?.conclusion_date || row?.request_approval_time);

const DEPOSIT_FINALIZED_STATUSES = new Set([2, 3, 4]);
const WITHDRAWAL_FINALIZED_STATUSES = new Set([3, 4, 5]);

/** Date used for period filter — finished date when finalized, else request date. */
export const getRowPeriodTimestamp = (row, requestType) => {
  const status = Number(row?.request_status);
  const finalizedStatuses =
    requestType === 0 ? DEPOSIT_FINALIZED_STATUSES : WITHDRAWAL_FINALIZED_STATUSES;

  if (finalizedStatuses.has(status)) {
    return getFinishedTimestamp(row) || parseFirestoreTimestamp(row?.date);
  }
  return parseFirestoreTimestamp(row?.date);
};

export const matchesFundsPeriodFilter = (row, startDate, endDate, requestType) => {
  if (!startDate || !endDate) return true;
  const ts = getRowPeriodTimestamp(row, requestType);
  if (!ts) return false;
  return isInDateRange(ts, startDate, endDate);
};

export const filterByFundsPeriod = (rows, startDate, endDate, requestType) => {
  if (!startDate || !endDate) return rows;
  return rows.filter((row) => matchesFundsPeriodFilter(row, startDate, endDate, requestType));
};

export const CLIENT_SEGMENT_OPTIONS = [
  { id: null, name: "All clients" },
  { id: "individual", name: "Individuals" },
  { id: "corporate", name: "Corporate" },
];

export const getDefaultFundsPeriodStart = () => dayjs().startOf("month");
export const getDefaultFundsPeriodEnd = () => dayjs();

export const filterByClientSegment = (rows, segment, usersMap = {}) => {
  if (!segment) return rows;
  return rows.filter((row) => {
    const userType = usersMap?.[row.user_id]?.user_type ?? row.user_type;
    const isCorporate = Number(userType) === 1;
    if (segment === "corporate") return isCorporate;
    if (segment === "individual") return !isCorporate;
    return true;
  });
};

export const hasActiveFundsFilters = ({
  search = "",
  status = null,
  clientSegment = null,
  dateFilterActive = false,
  periodStartDate,
  periodEndDate,
}) => {
  const defaultStart = getDefaultFundsPeriodStart();
  const defaultEnd = getDefaultFundsPeriodEnd();
  return (
    Boolean(search?.trim()) ||
    status !== null ||
    clientSegment !== null ||
    dateFilterActive ||
    !periodStartDate?.isSame(defaultStart, "day") ||
    !periodEndDate?.isSame(defaultEnd, "day")
  );
};

export const isCorporateUser = (user) => Number(user?.user_type) === 1;

/** Capital Routing legs move money between client accounts — not company cash in/out. */
export const isCapitalRoutingRow = (row) =>
  row?.is_internal_transfer === true ||
  Boolean(row?.capital_routing_id) ||
  row?.destination_type === "internal_capital_routing";

export const isInDateRange = (ts, startDate, endDate) => {
  if (!startDate || !endDate || !ts) return false;
  const value = typeof ts.isBetween === "function" ? ts : dayjs(ts);
  if (typeof value.isValid === "function" && !value.isValid()) return false;
  return value.isBetween(startDate.startOf("day"), endDate.endOf("day"), null, "[]");
};

export const getWithdrawalPaidAmount = (row) => {
  const offered = parseFloat(row?.offered_amount);
  if (!Number.isNaN(offered) && offered > 0) return offered;
  return parseFloat(row?.total_amount) || 0;
};

export const getDepositApprovedAmount = (row) => parseFloat(row?.total_amount) || 0;

export const formatFundsUsd = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export const PERIOD_TOTAL_LABELS = {
  deposit: {
    periodHeader: "Approved deposits",
    totalLabel: "Approved total",
    countLabel: "approved deposits",
    txnLabel: "approved",
    splitCaption: "split by client type",
  },
  withdrawal: {
    periodHeader: "Paid withdrawals",
    totalLabel: "Paid total",
    countLabel: "paid withdrawals",
    txnLabel: "paid",
    splitCaption: "split by client type",
  },
  monthlyGraph: {
    periodHeader: "Client profit",
    totalLabel: "Client profit total",
    countLabel: "monthly closures",
    txnLabel: "closures",
    splitCaption: "monthly performance split by client type",
  },
  yieldProducts: {
    periodHeader: "Net yield paid",
    totalLabel: "Net yield total",
    countLabel: "final payouts",
    txnLabel: "payouts",
    splitCaption: "yield finals split by client type",
  },
};

export const YIELD_PRODUCT_STATUS_OPTIONS = [
  { id: null, name: "All" },
  { id: "active", name: "Active" },
  { id: "finalized", name: "Finalized" },
  { id: "inactive", name: "Inactive" },
];

export const getMonthlyGraphDate = (row) => {
  const month = row?.graph_month;
  const year = row?.graph_year;
  if (year != null && month != null && month >= 0 && month <= 11) {
    return dayjs(new Date(year, month, 1, 12, 0, 0));
  }
  return parseFirestoreTimestamp(row?.graph_month_date || row?.start_date);
};

export const getMonthlyGraphClientProfit = (row) => parseFloat(row?.profit_to_user) || 0;

export const filterByMonthlyGraphPeriod = (rows, startDate, endDate) => {
  if (!startDate || !endDate) return rows;
  return rows.filter((row) => {
    const ts = getMonthlyGraphDate(row);
    return ts && isInDateRange(ts, startDate, endDate);
  });
};

export const getYieldFinalDate = (row) =>
  parseFirestoreTimestamp(row?.transaction_date || row?.processed_at);

export const getYieldFinalParticipantNet = (participant) =>
  parseFloat(participant?.net_yield ?? participant?.total_net_yield) || 0;

export const computeYieldFinalSegmentTotals = (records, { startDate, endDate }) => {
  const result = {
    individual: { total: 0, count: 0 },
    corporate: { total: 0, count: 0 },
  };

  for (const record of records) {
    const ts = getYieldFinalDate(record);
    if (!isInDateRange(ts, startDate, endDate)) continue;

    if (Array.isArray(record.participants) && record.participants.length > 0) {
      for (const participant of record.participants) {
        const amount = getYieldFinalParticipantNet(participant);
        const segment = Number(participant?.user_type) === 1 ? "corporate" : "individual";
        result[segment].total += amount;
        result[segment].count += 1;
      }
    } else {
      result.individual.total += parseFloat(record.individual_net_yield) || 0;
      result.individual.count += parseInt(record.individual_count, 10) || 0;
      result.corporate.total += parseFloat(record.corporate_net_yield) || 0;
      result.corporate.count += parseInt(record.corporate_count, 10) || 0;
    }
  }

  return result;
};

export const filterYieldProductsByPeriod = (rows, startDate, endDate) => {
  if (!startDate || !endDate) return rows;
  return rows.filter((row) => {
    const ts = parseFirestoreTimestamp(row?.start_date);
    return ts && isInDateRange(ts, startDate, endDate);
  });
};

export const productMatchesClientSegment = (product, segment, usersMap = {}) => {
  if (!segment) return true;
  const docId = product?.id;
  const fieldId = product?.product_id;
  let individual = 0;
  let corporate = 0;

  Object.values(usersMap).forEach((user) => {
    if (user?.yield_active !== true) return;
    const activeProductId = user?.yield_active_product_id;
    if (activeProductId !== docId && activeProductId !== fieldId) return;
    if (Number(user?.user_type) === 1) corporate += 1;
    else individual += 1;
  });

  if (segment === "corporate") return corporate > 0;
  if (segment === "individual") return individual > 0;
  return true;
};

export const matchesYieldProductStatus = (product, statusFilter, finalSubmissions = new Map()) => {
  if (!statusFilter) return true;
  const isFinalized =
    finalSubmissions.get(product?.product_id) || finalSubmissions.get(product?.id) || false;

  if (statusFilter === "finalized") return isFinalized;
  if (statusFilter === "active") return product?.active === true && !isFinalized;
  if (statusFilter === "inactive") return product?.active !== true && !isFinalized;
  return true;
};

export const computeSegmentTotals = (
  transactions,
  usersMap,
  { startDate, endDate, getAmount, getDate = getFinishedTimestamp }
) => {
  const result = {
    individual: { total: 0, count: 0 },
    corporate: { total: 0, count: 0 },
    routing: { total: 0, count: 0 },
  };

  for (const txn of transactions) {
    const rowDate = getDate(txn);
    if (!isInDateRange(rowDate, startDate, endDate)) continue;

    const amount = getAmount(txn);
    if (isCapitalRoutingRow(txn)) {
      result.routing.total += amount;
      result.routing.count += 1;
      continue;
    }

    const user = usersMap?.[txn.user_id];
    const segment = isCorporateUser(user) ? "corporate" : "individual";
    result[segment].total += amount;
    result[segment].count += 1;
  }

  return result;
};
